import {Card, Container, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback} from "react";
import {ABACPolicyEntity} from "@ots-frontend/ots-data";
import {ABACPolicyDetails} from "./ABACPolicyDetails";
import {createContentStyles} from "../../../theme/useContentStyles";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {ABACPolicyAction, ABACPolicyActionMenuDropdown} from "../../menu/ABACPolicyActionMenu/ABACPolicyActionMenu";
import {useABACPolicyMutation} from "../../../hooks/useABACPolicyMutation";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {TabItem} from "../../tabs/Tabs/Tabs";
import { ObjectChangeLogView } from "../common/ObjectChangeLogView/ObjectChangeLogView";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type ABACPolicyProfileProps = PropsWithChildren<{
  abacPolicy: ABACPolicyEntity
}>

const tabs: TabItem[] = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: "history",
    value: "Change Logs",
    permissions: ["change_log_view"]
  },
];

export const ABACPolicyProfile = (props: ABACPolicyProfileProps) => {
  const {abacPolicy} = props;

  const {classes} = useStyles();

  const {t} = useTranslation()
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const {
    deleteABACPolicy
  } = useABACPolicyMutation()

  const onActionClick = useCallback((action: ABACPolicyAction) => {
    if (action === 'EDIT') {
      navigate(`/abac-policies/form`, {state: {id: abacPolicy.id}})
    } else if (action === 'DELETE') {
      deleteABACPolicy(abacPolicy.id, () => {
        navigate(-1)
      })
    }
  }, [abacPolicy, navigate, deleteABACPolicy])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>ABAC Policy: {abacPolicy.name}</Title>

          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <ABACPolicyActionMenuDropdown abacPolicy={abacPolicy} onActionClick={onActionClick}/>
            </Menu.Dropdown>
          </Menu>

        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(pathname + search, {
              replace: true,
              state: {...state, tabValue: value}
            })}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <ABACPolicyDetails abacPolicy={abacPolicy}/>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={abacPolicy}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
